import {
  RISK_CATEGORY,
  RISK_MATH,
  RISK_STATUS,
  RISK_TREATMENT,
  Risk,
} from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { Assignee } from 'src/Utils/globalEnums'
import {
  CSV_VALIDATIONS,
  CSV_COLUMN_VALIDATIONS,
  ParseColumnType,
  RowValidator,
} from 'src/Utils/csv/csv.constants'
import {
  RISK_MATH_MAP,
  RISK_STATUS_LABEL,
  RiskStatusLabels,
} from '../risks.constants'
import { ImportRiskCSVRow } from './RiskModals.constants'

export const getRiskMathValueAsString = (value: string): string => {
  switch (value.trim().toLowerCase()) {
    case 'very high':
      return RISK_MATH.VERY_HIGH.toString()
    case 'high':
      return RISK_MATH.HIGH.toString()
    case 'moderate':
      return RISK_MATH.MODERATE.toString()
    case 'low':
      return RISK_MATH.LOW.toString()
    case 'very low':
      return RISK_MATH.VERY_LOW.toString()
    default:
      return RISK_MATH.NOT_SET.toString()
  }
}

export const getRiskStatusValueAsString = (value: string): string => {
  switch (value.trim().toLowerCase()) {
    case 'to do':
      return RISK_STATUS_LABEL[RISK_STATUS.TODO]
    case 'in progress':
      return RISK_STATUS_LABEL[RISK_STATUS.IN_PROGRESS]
    case 'pending':
      return RISK_STATUS_LABEL[RISK_STATUS.PENDING]
    case 'completed':
      return RISK_STATUS_LABEL[RISK_STATUS.COMPLETED]
    default:
      return RISK_STATUS_LABEL[RISK_STATUS.TODO]
  }
}

export const getTreatmentValueAsString = (value: string): string => {
  switch (value.trim().toLowerCase()) {
    case 'accept':
      return RISK_TREATMENT.ACCEPT.toString()
    case 'avoid':
      return RISK_TREATMENT.AVOID.toString()
    case 'mitigate':
      return RISK_TREATMENT.MITIGATE.toString()
    case 'share':
      return RISK_TREATMENT.SHARE.toString()
    case 'transfer':
      return RISK_TREATMENT.TRANSFER.toString()
    default:
      return ''
  }
}

export const getRiskCategoryValueAsString = (value: string): string => {
  switch (value.trim().toLowerCase()) {
    case 'customer commitments':
      return RISK_CATEGORY.CUSTOMER.toString()
    case 'vendor management':
      return RISK_CATEGORY.VENDOR.toString()
    case 'leadership commitment':
      return RISK_CATEGORY.LEADERSHIP.toString()
    case 'systems and technology changes':
      return RISK_CATEGORY.SYSTEMS.toString()
    case 'laws, regulations, and standards':
      return RISK_CATEGORY.LAWS.toString()
    case 'intellectual property':
      return RISK_CATEGORY.IP.toString()
    default:
      return ''
  }
}

const RISK_MATH_VALUES = [
  'very high',
  'high',
  'moderate',
  'low',
  'very low',
  'not set',
]

const RISK_TREATMENT_VALUES = [
  'accept',
  'avoid',
  'mitigate',
  'share',
  'transfer',
]

const RISK_CATEGORY_VALUES = [
  'customer commitments',
  'vendor management',
  'leadership commitment',
  'systems and technology changes',
  'laws, regulations, and standards',
  'intellectual property',
]

export const getRiskParsers = (
  userEmailSet: Set<string>,
  controlIdSet: Set<string>,
): ParseColumnType => ({
  riskId: {
    validator: (columnName, value, row) =>
      CSV_VALIDATIONS.LENGTH(columnName, value, 1, 10, row),
    mutator: (value: string) => value.slice(0, 10),
  },
  threat: {
    validator: (columnName, value, row) =>
      CSV_VALIDATIONS.LENGTH(columnName, value, 1, 255, row),
    mutator: (value: string) => value.slice(0, 255),
  },
  category: {
    validator: (_c, value, row) =>
      CSV_VALIDATIONS.VALUE_IN_ARRAY(value, row, RISK_CATEGORY_VALUES),
    mutator: (value) => getRiskCategoryValueAsString(value),
  },
  predisposingCondition: {
    validator: (_c, _v, _r) => null,
    mutator: (value) => value,
  },
  assetAtRisk: {
    validator: (_c, _v, _r) => null,
    mutator: (value) => value,
  },
  impactToCustomer: {
    validator: (_c, _v, _r) => null,
    mutator: (value) => value,
  },
  likelihood: {
    validator: (_c, value, row) =>
      CSV_VALIDATIONS.VALUE_IN_ARRAY(value, row, RISK_MATH_VALUES),
    mutator: (value) => getRiskMathValueAsString(value),
  },
  impact: {
    validator: (_c, value, row) =>
      CSV_VALIDATIONS.VALUE_IN_ARRAY(value, row, RISK_MATH_VALUES),
    mutator: (value: string) => getRiskMathValueAsString(value),
  },
  inherentRisk: {
    validator: (_c, value, row) =>
      CSV_VALIDATIONS.VALUE_IN_ARRAY(value, row, RISK_MATH_VALUES),
    mutator: (value) => getRiskMathValueAsString(value),
  },
  response: {
    validator: (_c, value, row) =>
      CSV_VALIDATIONS.VALUE_IN_ARRAY(value, row, RISK_TREATMENT_VALUES),
    mutator: (value) => getTreatmentValueAsString(value),
  },
  riskOwner: {
    validator: (_c, value, row) =>
      CSV_VALIDATIONS.USER_EMAIL(value, userEmailSet, row),
    mutator: (value) => {
      if (value.trim().toLowerCase() === Assignee.UNASSIGNED.toLowerCase()) {
        return ''
      }
      return value
    },
  },
  targetImpact: {
    validator: (_c, value, row) =>
      CSV_VALIDATIONS.VALUE_IN_ARRAY(value, row, RISK_MATH_VALUES),
    mutator: (value) => getRiskMathValueAsString(value),
  },
  targetLikelihood: {
    validator: (_c, value, row) =>
      CSV_VALIDATIONS.VALUE_IN_ARRAY(value, row, RISK_MATH_VALUES),
    mutator: (value) => getRiskMathValueAsString(value),
  },
  residualRisk: {
    validator: (_c, value, row) =>
      CSV_VALIDATIONS.VALUE_IN_ARRAY(value, row, RISK_MATH_VALUES),
    mutator: (value) => getRiskMathValueAsString(value),
  },
  currentResidualRisk: {
    validator: (_c, value, row) =>
      CSV_VALIDATIONS.VALUE_IN_ARRAY(value, row, RISK_MATH_VALUES),
    mutator: (value) => getRiskMathValueAsString(value),
  },
  status: {
    validator: (_c, value, row) =>
      CSV_VALIDATIONS.VALUE_IN_ARRAY(
        value,
        row,
        Object.values(RiskStatusLabels),
      ),
    mutator: (value) => getRiskStatusValueAsString(value),
  },
  controlIds: {
    validator: (_, value, row) =>
      CSV_VALIDATIONS.ID_EXISTS(value, controlIdSet, row) ||
      CSV_VALIDATIONS.ID_EXISTS_ONCE(row, value),
    mutator: (value) => value.split(',').map((v) => v.trim()),
  },
  createdDate: {
    validator: (columnName, value, row) =>
      CSV_VALIDATIONS.DATE(columnName, value, row),
    mutator: (value) => value,
  },
  closedDate: {
    validator: (columnName, value, row) =>
      CSV_VALIDATIONS.DATE(columnName, value, row),
    mutator: (value) => value,
  },
})

const safeNumber = (value: string, defaultValue = 0): number => {
  if (!value || value.trim() === '') {
    return defaultValue
  }
  const num = Number(value)
  return isNaN(num) ? defaultValue : num
}

export const prepareRisksUpload = (
  rows: ImportRiskCSVRow[],
  controlsMap: { [x: string]: string },
): Risk[] =>
  rows.map((row) => {
    const risk = new Risk()
      .setCustomId(row.riskId)
      .setName(row.threat)
      .setCategory(safeNumber(row.category, RISK_CATEGORY.NO_CATEGORY))
      .setPredisposingCondition(row.predisposingCondition)
      .setAssetAtRisk(row.assetAtRisk)
      .setImpactToCustomer(row.impactToCustomer)
      .setLikelihood(safeNumber(row.likelihood, RISK_MATH.NOT_SET) as RISK_MATH)
      .setAdverseImpact(safeNumber(row.impact, RISK_MATH.NOT_SET) as RISK_MATH)
      .setOwnerEmail(row.riskOwner)
      .setTargetAdverseImpact(
        safeNumber(row.targetImpact, RISK_MATH.NOT_SET) as RISK_MATH,
      )
      .setTargetLikelihood(
        safeNumber(row.targetLikelihood, RISK_MATH.NOT_SET) as RISK_MATH,
      )
      .setRiskAfterTreatment(
        safeNumber(row.residualRisk, RISK_MATH.NOT_SET) as RISK_MATH,
      )
      .setStatus(safeNumber(row.status, RISK_STATUS.TODO) as RISK_STATUS)
      .setCreatedAt(row.createdDate ? new Date(row.createdDate).getTime() : 0)
      .setClosedAt(row.closedDate ? new Date(row.closedDate).getTime() : 0)
      .setControlIds(
        row.controlIds.map((controlId) => controlsMap[controlId]).join(','),
      )
      .setNotes(row.notes || '')
    if (row.response) {
      risk.setTreatment(
        safeNumber(row.response, RISK_TREATMENT.ACCEPT) as RISK_TREATMENT,
      )
    }
    return risk
  })

export const getRiskRowValidators = (): RowValidator[] => {
  const validateInherentRisk: RowValidator = (
    row: Record<string, string>,
    rowNumber: number,
  ) => {
    const inherentRisk = Number(
      getRiskMathValueAsString(row.inherentRisk),
    ) as RISK_MATH
    const impact = row.impact
    const likelihood = row.likelihood
    const calculatedInherentRisk =
      RISK_MATH_MAP[Number(getRiskMathValueAsString(impact)) as RISK_MATH][
        Number(getRiskMathValueAsString(likelihood)) as RISK_MATH
      ]
    if (inherentRisk !== calculatedInherentRisk) {
      return `Row ${rowNumber}: Inherent Risk is not calculated correctly. The calculated inherent risk is ${calculatedInherentRisk} but the provided inherent risk is ${inherentRisk}.`
    }
    return null
  }
  const validateResidualRisk: RowValidator = (
    row: Record<string, string>,
    rowNumber: number,
  ) => {
    const residualRisk = Number(
      getRiskMathValueAsString(row.residualRisk),
    ) as RISK_MATH
    const targetImpact = row.targetImpact
    const targetLikelihood = row.targetLikelihood
    const calculatedResidualRisk =
      RISK_MATH_MAP[
        Number(getRiskMathValueAsString(targetImpact)) as RISK_MATH
      ][Number(getRiskMathValueAsString(targetLikelihood)) as RISK_MATH]
    if (residualRisk !== calculatedResidualRisk) {
      return `Row ${rowNumber}: Residual Risk is not calculated correctly. The calculated residual risk is ${calculatedResidualRisk} but the provided residual risk is ${residualRisk}.`
    }
    return null
  }

  // Add duplicate check for Risk ID column
  const validateDuplicateRiskId = CSV_COLUMN_VALIDATIONS.DUPLICATE_VALUES(
    'riskId',
    'Risk ID',
  )

  return [validateInherentRisk, validateResidualRisk, validateDuplicateRiskId]
}
